'use client';

import { memo } from 'react';
import type { FC } from 'react';
import { CaseCardBase } from './CaseCardBase';
import { Credits } from '../Credits';
import { BonusCredits } from '../BonusCredits';
import { OverlayBadge } from '../ui';
import { formatCreditsShort } from '../../utils/formatters';
import { FaFire, GiOpenChest } from '../icons';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './CaseCard.translations';

interface CaseCardProps {
  id: string;
  name: string;
  imageUrl: string;
  imagePlaceholder?: string | null;
  priceInCredits?: number;
  openingsCount?: number;
  isNew?: boolean;
  isPopular?: boolean;
  bonusCurrency?: boolean;
  href?: string;
  onClick?: (id: string) => void;
  priority?: boolean;
  imageSizes?: string;
}

const CaseCardComponent: FC<CaseCardProps> = ({
  id,
  name,
  imageUrl,
  imagePlaceholder,
  priceInCredits,
  openingsCount,
  isNew,
  isPopular,
  bonusCurrency = false,
  href,
  onClick,
  priority = false,
  imageSizes,
}) => {
  const { t } = useLocalTranslation(translations);

  const topSlot =
    isNew || isPopular ? (
      <div className="flex items-center gap-1">
        {isPopular && (
          <OverlayBadge>
            <FaFire size={11} className="me-1 text-accent-orange" />
            <span className="text-sm font-semibold text-white">{t('popularBadge')}</span>
          </OverlayBadge>
        )}
        {isNew && (
          <OverlayBadge className="bg-accent-red">
            <span className="text-sm font-semibold text-white">{t('newBadge')}</span>
          </OverlayBadge>
        )}
      </div>
    ) : null;

  const bottomSlot = (
    <>
      <OverlayBadge>
        <span className="text-sm font-semibold text-white">{name}</span>
      </OverlayBadge>

      <div className="flex flex-row items-center gap-1.5">
        {priceInCredits !== undefined && (
          <OverlayBadge>
            {bonusCurrency ? <BonusCredits amount={priceInCredits} compact /> : <Credits amount={priceInCredits} compact />}
          </OverlayBadge>
        )}
        {openingsCount !== undefined && (
          <OverlayBadge>
            <GiOpenChest style={{ marginRight: '4px' }} />
            <span className="text-sm font-semibold text-white">{formatCreditsShort(openingsCount)}</span>
          </OverlayBadge>
        )}
      </div>
    </>
  );

  return (
    <CaseCardBase
      imageUrl={imageUrl}
      imagePlaceholder={imagePlaceholder}
      alt={name}
      href={href}
      onClick={() => onClick?.(id)}
      topSlot={topSlot}
      bottomSlot={bottomSlot}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      imageSizes={imageSizes}
    />
  );
};

export const CaseCard = memo(CaseCardComponent);
